import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, ClipboardCheck, CheckCircle, XCircle, User, MapPin, Gauge, Eye } from 'lucide-react';
import { getChecklists, createChecklist, updateChecklist, deleteChecklist, getVehicles } from '../api';
import { useAuth } from '../AuthContext';

const STATUS_OPTIONS = ['COMPLETED', 'PENDING', 'CANCELLED'];
const KONDISI_OPTIONS = ['Baik', 'Rusak', 'Perlu Perbaikan'];

const DEFAULT_CHECKLIST = [
  { nama: 'Level Minyak Rem', kondisi: 'Baik', keterangan: '' },
  { nama: 'Level Minyak Setir', kondisi: 'Baik', keterangan: '' },
  { nama: 'Air Radiator', kondisi: 'Baik', keterangan: '' },
  { nama: 'Aki & Terminal', kondisi: 'Baik', keterangan: '' },
  { nama: 'Ban & Tekanan Angin', kondisi: 'Baik', keterangan: '' },
  { nama: 'Lampu-Lampu Depan', kondisi: 'Baik', keterangan: '' },
  { nama: 'Lampu-Lampu Belakang', kondisi: 'Baik', keterangan: '' },
  { nama: 'Kopling & Gas', kondisi: 'Baik', keterangan: '' },
  { nama: 'Wiper & Air Wiper', kondisi: 'Baik', keterangan: '' },
  { nama: 'Klakson', kondisi: 'Baik', keterangan: '' },
  { nama: 'Spion (Kiri & Kanan)', kondisi: 'Baik', keterangan: '' },
  { nama: 'Sabuk Pengaman', kondisi: 'Baik', keterangan: '' },
  { nama: 'Dashboard Instrumen', kondisi: 'Baik', keterangan: '' },
  { nama: 'Body & Cat', kondisi: 'Baik', keterangan: '' },
  { nama: 'Interior & Kebersihan', kondisi: 'Baik', keterangan: '' },
];

const emptyForm = {
  vehicle_id: '', no_polisi: '', no_body: '', check_date: '',
  officer_name: '', location: '', mileage: '',
  check_items: DEFAULT_CHECKLIST.map(i => ({ ...i })), notes: '', status: 'COMPLETED',
};

// ======== Status Badge ========
function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  let bg = '#fef3c7', color = '#92400e';
  if (s === 'completed') { bg = '#d1fae5'; color = '#065f46'; }
  if (s === 'cancelled') { bg = '#fee2e2'; color = '#991b1b'; }
  return <span className="badge" style={{ background: bg, color }}>{status || '-'}</span>;
}

// ======== Detail Modal ========
function DetailModal({ item, onClose }) {
  if (!item) return null;

  const kondisiCount = (k) => item.check_items?.filter(i => i.kondisi === k).length || 0;
  const baik = kondisiCount('Baik');
  const rusak = kondisiCount('Rusak');
  const perlu = kondisiCount('Perlu Perbaikan');
  const total = item.check_items?.length || 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h3><ClipboardCheck size={18} style={{ marginRight: 8 }} />Detail Checklist</h3>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>No. Polisi</label>
              <div style={{ padding: '8px 0', fontWeight: 600 }}>{item.no_polisi || '-'}</div>
            </div>
            <div className="form-group">
              <label>Tanggal</label>
              <div style={{ padding: '8px 0' }}>{item.check_date || '-'}</div>
            </div>
            <div className="form-group">
              <label>Petugas</label>
              <div style={{ padding: '8px 0' }}>{item.officer_name || '-'}</div>
            </div>
            <div className="form-group">
              <label>Lokasi</label>
              <div style={{ padding: '8px 0' }}>{item.location || '-'}</div>
            </div>
            <div className="form-group">
              <label>Kilometer</label>
              <div style={{ padding: '8px 0' }}>{item.mileage ? `${item.mileage} km` : '-'}</div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div style={{ padding: '4px 0' }}><StatusBadge status={item.status} /></div>
            </div>
          </div>

          {/* Summary */}
          {total > 0 && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, padding: 12, background: 'var(--gray-50)', borderRadius: 8 }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2dce89' }}>{baik}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Baik</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#fb6340' }}>{perlu}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Perlu Perbaikan</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f5365c' }}>{rusak}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--gray-400)', textTransform: 'uppercase' }}>Rusak</div>
              </div>
            </div>
          )}

          {/* Checklist Items */}
          <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 12 }}>
            <label style={{ fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--gray-400)', display: 'block', marginBottom: 8 }}>
              Item Pemeriksaan ({total})
            </label>
            {item.check_items?.map((ci, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--gray-50)' }}>
                {ci.kondisi === 'Baik' ? <CheckCircle size={14} color="#2dce89" /> :
                 ci.kondisi === 'Rusak' ? <XCircle size={14} color="#f5365c" /> :
                 <AlertCircle size={14} color="#fb6340" />}
                <span style={{ flex: 1, fontSize: '0.85rem' }}>{ci.nama}</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 500, color: ci.kondisi === 'Baik' ? '#2dce89' : ci.kondisi === 'Rusak' ? '#f5365c' : '#fb6340' }}>
                  {ci.kondisi}
                </span>
                {ci.keterangan && <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontStyle: 'italic' }}>({ci.keterangan})</span>}
              </div>
            ))}
          </div>

          {item.notes && (
            <div style={{ marginTop: 12, padding: 12, background: '#fff8f0', borderRadius: 8, border: '1px solid #fed7aa' }}>
              <small style={{ fontWeight: 600, color: '#92400e' }}>Catatan:</small>
              <p style={{ fontSize: '0.85rem', color: '#78350f', marginTop: 4 }}>{item.notes}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Tutup</button>
        </div>
      </div>
    </div>
  );
}

// ======== Main Page ========
export default function Checklist() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [detailItem, setDetailItem] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    Promise.all([getChecklists(params), getVehicles({ limit: 100 })])
      .then(([res, vehRes]) => { setData(res.data); setVehicles(vehRes.data?.data || []); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = data.filter(d =>
    !search || d.no_polisi?.toLowerCase().includes(search.toLowerCase()) ||
    d.officer_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.location?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      check_date: new Date().toISOString().slice(0, 10),
      check_items: DEFAULT_CHECKLIST.map(i => ({ ...i })),
    });
    setError('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      vehicle_id: item.vehicle_id ?? '',
      no_polisi: item.no_polisi || '',
      no_body: item.no_body || '',
      check_date: item.check_date || '',
      officer_name: item.officer_name || '',
      location: item.location || '',
      mileage: item.mileage ?? '',
      check_items: item.check_items?.length > 0 ? item.check_items.map(i => ({ ...i })) : DEFAULT_CHECKLIST.map(i => ({ ...i })),
      notes: item.notes || '',
      status: item.status || 'COMPLETED',
    });
    setError('');
    setShowModal(true);
  };

  const updateChecklistItem = (idx, field, value) => {
    const items = [...form.check_items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, check_items: items });
  };

  const addChecklistItem = () => {
    setForm({
      ...form,
      check_items: [...form.check_items, { nama: '', kondisi: 'Baik', keterangan: '' }],
    });
  };

  const removeChecklistItem = (idx) => {
    if (form.check_items.length <= 1) return;
    setForm({
      ...form,
      check_items: form.check_items.filter((_, i) => i !== idx),
    });
  };

  const handleVehicleSelect = (e) => {
    const vid = e.target.value;
    if (vid) {
      const v = vehicles.find(v => v.id === Number(vid));
      if (v) setForm(f => ({ ...f, vehicle_id: vid, no_polisi: v.plate_no || '', no_body: v.body_no || '' }));
    } else {
      setForm(f => ({ ...f, vehicle_id: '', no_polisi: '', no_body: '' }));
    }
  };

  const handleSave = async () => {
    setError('');
    if (!form.check_date) { setError('Tanggal pemeriksaan harus diisi'); return; }
    if (!form.officer_name) { setError('Nama petugas harus diisi'); return; }
    if (!form.vehicle_id && !form.no_polisi) { setError('Pilih kendaraan atau isi no polisi'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        vehicle_id: form.vehicle_id ? Number(form.vehicle_id) : null,
        mileage: form.mileage ? Number(form.mileage) : null,
      };
      if (editing) {
        await updateChecklist(editing.id, payload);
      } else {
        await createChecklist(payload);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin ingin menghapus checklist ${item.no_polisi || ''}?`)) return;
    try { await deleteChecklist(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal menghapus'); }
  };

  const getKondisiIcon = (kondisi) => {
    if (kondisi === 'Baik') return <CheckCircle size={14} color="#2dce89" />;
    if (kondisi === 'Rusak') return <XCircle size={14} color="#f5365c" />;
    return <AlertCircle size={14} color="#fb6340" />;
  };

  const summaryKondisi = (items) => {
    if (!items?.length) return { baik: 0, rusak: 0, perlu: 0 };
    return {
      baik: items.filter(i => i.kondisi === 'Baik').length,
      rusak: items.filter(i => i.kondisi === 'Rusak').length,
      perlu: items.filter(i => i.kondisi === 'Perlu Perbaikan').length,
    };
  };

  // Stats
  const totalChecklists = data.length;
  const statsKondisi = data.reduce((acc, d) => {
    const s = summaryKondisi(d.check_items);
    acc.rusak += s.rusak;
    acc.perlu += s.perlu;
    return acc;
  }, { rusak: 0, perlu: 0 });

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Checklist Pemeriksaan Harian</h2>
            <p className="page-desc">{totalChecklists} pemeriksaan tercatat</p>
          </div>
          {hasMenuAction('checklist', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /><span>Buat Checklist Baru</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="dash-stats-row" style={{ marginBottom: 20 }}>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Total Pemeriksaan</h5>
                <span className="stat-card-value">{totalChecklists}</span>
                <span className="stat-card-sublabel">Semua status</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#11cdef18', color: '#11cdef' }}>
                  <ClipboardCheck size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Item Rusak</h5>
                <span className="stat-card-value" style={{ color: '#f5365c' }}>{statsKondisi.rusak}</span>
                <span className="stat-card-sublabel">Butuh perbaikan segera</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#f5365c18', color: '#f5365c' }}>
                  <XCircle size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Perlu Perbaikan</h5>
                <span className="stat-card-value" style={{ color: '#fb6340' }}>{statsKondisi.perlu}</span>
                <span className="stat-card-sublabel">Perlu ditindaklanjuti</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#fb634018', color: '#fb6340' }}>
                  <AlertCircle size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dash-stat-col">
          <div className="stat-card">
            <div className="stat-card-body">
              <div className="stat-card-content">
                <h5 className="stat-card-label">Total Kendaraan Diperiksa</h5>
                <span className="stat-card-value">{new Set(data.filter(d => d.no_polisi).map(d => d.no_polisi)).size}</span>
                <span className="stat-card-sublabel">Unit berbeda</span>
              </div>
              <div className="stat-card-icon-col">
                <div className="stat-icon-shape" style={{ background: '#2dce8918', color: '#2dce89' }}>
                  <CheckCircle size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input type="text" placeholder="Cari no polisi, petugas, lokasi..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Semua Status</option>
          {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o === 'COMPLETED' ? 'Selesai' : o === 'PENDING' ? 'Pending' : 'Dibatalkan'}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="page-loading"><div className="spinner-lg" /><p>Memuat data...</p></div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
          {filtered.map(d => {
            const s = summaryKondisi(d.check_items);
            const totalItems = d.check_items?.length || 0;
            return (
              <div key={d.id} className="region-card">
                <div className="region-card-header" style={{
                  background: d.status === 'COMPLETED' ? 'linear-gradient(135deg, #2dce89, #1aae6f)' :
                             d.status === 'CANCELLED' ? 'linear-gradient(135deg, #8898aa, #6c7a8d)' :
                             'linear-gradient(135deg, #fb6340, #e64a2e)'
                }}>
                  <ClipboardCheck size={22} />
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '0.95rem' }}>{d.no_polisi || 'Tanpa Kendaraan'}</h3>
                    <small style={{ opacity: 0.8 }}>{d.check_date || '-'}</small>
                  </div>
                  <StatusBadge status={d.status === 'COMPLETED' ? 'Selesai' : d.status === 'CANCELLED' ? 'Dibatalkan' : 'Pending'} />
                </div>
                <div className="region-card-body" style={{ padding: 16 }}>
                  <div className="region-detail"><User size={14} className="text-muted" /><strong>Petugas:</strong> {d.officer_name || '-'}</div>
                  <div className="region-detail"><MapPin size={14} className="text-muted" /><strong>Lokasi:</strong> {d.location || '-'}</div>
                  {d.mileage && <div className="region-detail"><Gauge size={14} className="text-muted" /><strong>KM:</strong> {d.mileage?.toLocaleString()} km</div>}

                  {/* Mini status bar */}
                  {totalItems > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 2, height: 4 }}>
                      <div style={{ flex: s.baik, background: '#2dce89', borderRadius: '2px 0 0 2px', minWidth: s.baik > 0 ? 4 : 0 }} />
                      <div style={{ flex: s.perlu, background: '#fb6340', minWidth: s.perlu > 0 ? 4 : 0 }} />
                      <div style={{ flex: s.rusak, background: '#f5365c', borderRadius: '0 2px 2px 0', minWidth: s.rusak > 0 ? 4 : 0 }} />
                    </div>
                  )}

                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {s.baik > 0 && <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>{s.baik} Baik</span>}
                    {s.perlu > 0 && <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>{s.perlu} Perlu Perbaikan</span>}
                    {s.rusak > 0 && <span className="badge" style={{ background: '#fee2e2', color: '#991b1b' }}>{s.rusak} Rusak</span>}
                    {totalItems === 0 && <span className="badge" style={{ background: 'var(--gray-100)', color: 'var(--gray-500)' }}>0 item</span>}
                  </div>

                  {/* Quick preview of items */}
                  {d.check_items?.slice(0, 4).map((ci, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', padding: '2px 0', marginTop: 2 }}>
                      {getKondisiIcon(ci.kondisi)}
                      <span style={{ flex: 1, color: 'var(--gray-500)' }}>{ci.nama}</span>
                    </div>
                  ))}
                  {(d.check_items?.length || 0) > 4 && (
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
                      +{d.check_items.length - 4} item lainnya
                    </div>
                  )}

                  <div className="action-btns" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                    <button className="btn-icon" title="Detail" onClick={() => setDetailItem(d)}><Eye size={14} /></button>
                    {hasMenuAction('checklist', 'edit') && (
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>
                    )}
                    {hasMenuAction('checklist', 'delete') && (
                      <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="page-loading" style={{ gridColumn: '1/-1' }}>Belum ada data checklist</div>}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Checklist' : 'Buat Checklist Baru'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* Form Fields */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Kendaraan</label>
                  <select value={form.vehicle_id} onChange={handleVehicleSelect}>
                    <option value="">-- Pilih Kendaraan --</option>
                    {vehicles.map(v => (
                      <option key={v.id} value={v.id}>{v.plate_no} - {v.body_no}</option>
                    ))}
                  </select>
                  <small className="form-hint">Atau isi manual di bawah</small>
                </div>
                <div className="form-group">
                  <label>No. Polisi</label>
                  <input type="text" value={form.no_polisi} onChange={e => setForm({...form, no_polisi: e.target.value})} placeholder="B 1234 ABC" />
                </div>
                <div className="form-group">
                  <label>Tanggal Pemeriksaan *</label>
                  <input type="date" value={form.check_date} onChange={e => setForm({...form, check_date: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Nama Petugas *</label>
                  <input type="text" value={form.officer_name} onChange={e => setForm({...form, officer_name: e.target.value})} placeholder="Nama petugas" />
                </div>
                <div className="form-group">
                  <label>Lokasi</label>
                  <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Lokasi pemeriksaan" />
                </div>
                <div className="form-group">
                  <label>Kilometer (km)</label>
                  <input type="number" value={form.mileage} onChange={e => setForm({...form, mileage: e.target.value})} placeholder="Odometer" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o === 'COMPLETED' ? 'Selesai' : o === 'PENDING' ? 'Pending' : 'Dibatalkan'}</option>)}
                  </select>
                </div>
              </div>

              {/* Dynamic Checklist Items */}
              <div style={{ marginTop: 20, borderTop: '1px solid var(--gray-100)', paddingTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase', color: 'var(--gray-400)' }}>
                    Item Pemeriksaan ({form.check_items.length})
                  </label>
                  <button type="button" onClick={addChecklistItem} className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '0.78rem' }}>
                    <Plus size={12} /> Tambah Item
                  </button>
                </div>

                <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 8 }}>
                  {form.check_items.map((ci, idx) => (
                    <div key={idx} style={{
                      display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, padding: 6,
                      background: idx % 2 === 0 ? 'var(--gray-50)' : 'transparent',
                      borderRadius: 6,
                    }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', minWidth: 24 }}>{idx + 1}.</span>
                      <input
                        type="text"
                        value={ci.nama}
                        onChange={e => updateChecklistItem(idx, 'nama', e.target.value)}
                        placeholder="Nama item"
                        style={{ flex: 2, padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.82rem', minWidth: 120 }}
                      />
                      <select
                        value={ci.kondisi}
                        onChange={e => updateChecklistItem(idx, 'kondisi', e.target.value)}
                        style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.82rem' }}
                      >
                        {KONDISI_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <input
                        type="text"
                        value={ci.keterangan}
                        onChange={e => updateChecklistItem(idx, 'keterangan', e.target.value)}
                        placeholder="Ket."
                        style={{ flex: 2, padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 6, fontSize: '0.82rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(idx)}
                        className="btn-icon btn-danger"
                        title="Hapus item"
                        style={{ flexShrink: 0 }}
                        disabled={form.check_items.length <= 1}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Catatan</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Catatan tambahan..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <DetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </div>
  );
}
